import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { FilterBar } from '@/shared/ui/filters'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { cn } from '@/lib/utils'
import { UNIT_TYPE_LABEL, UNIT_TYPE_ORDER, UNIT_TYPE_PLURAL } from '@/shared/api/types'
import type { SurveyGeoCellDto } from '@/shared/api/types'
import { getInstrument, getPoll, getResponseGeo, listPolls } from './api'
import { PollSelect } from './PollSelect'
import { CellSwatch, GeoCellMap, NO_LEADER_INK, optionColours } from './GeoCellMap'
import { count, num, pct } from './vocab'

/**
 * Where the fieldwork landed — as geography, not as people.
 *
 * ## The one thing this screen must never become
 *
 * It is an **aggregate map**. Every point on it is `centroidLat`/`centroidLng`,
 * the centroid of the county, constituency, ward or polling-station catchment an
 * interview was attributed to. A respondent's own fix is written by
 * `POST /surveys/{id}/responses`, turned into a JTS `Point`, and never read back
 * by any endpoint — the geo read is the only geographic aggregate the API has,
 * and it deals in units.
 *
 * That is why a cell holding fewer than `minCellBase` interviews is not returned
 * at all rather than drawn small: one interview in a ward, plotted with a leading
 * candidate, **is** the respondent, and no amount of rounding on the client fixes
 * that. The floor is disclosure control applied server-side; `suppressedCells`
 * is how many it took, and the KPI strip names both the number and the floor.
 *
 * The disclosure sits in visible copy above the map, once. Not in a tooltip: a
 * caveat nobody can see until they hover is a caveat that does not exist, and
 * the misreading this screen risks — "these dots are the people we interviewed" —
 * is the kind somebody acts on without ever asking a question.
 *
 * ## Level is a choice, and the client does not make it
 *
 * `level` is required by the server and has no default there. It is not a client
 * zoom either — each level is a different aggregation and a different disclosure
 * outcome: the same fieldwork withholds a different number of cells at ward level
 * than at county level. So this screen does not pick one on the reader's behalf;
 * it says why, and the four codes come from `UNIT_TYPE_ORDER` — the hierarchy
 * constant the geography module already uses — rather than a fresh literal list
 * written beside it. Anything else in the URL is sent to the server unchanged,
 * and the server's sentence is what the reader sees.
 *
 * ## The table is not a fallback
 *
 * It is the same cells, in the accessible view, and it renders whether or not a
 * basemap does. This deployment has no Mapbox token, so that is the path that
 * actually runs.
 */
export function MapPage() {
  const canRead = useCan('survey:read')
  const [params, setParams] = useSearchParams()
  const pollId = params.get('poll')
  /** Raw, unvalidated. Rule 3 — offer the control and let the server refuse. */
  const level = params.get('level') ?? ''
  const questionId = params.get('question') ?? ''

  const [selected, setSelected] = useState<string | null>(null)
  const rows = useRef(new Map<string, HTMLTableRowElement | null>())

  const polls = useLoad(() => listPolls({}, 0, 100), [])
  // By id, not found in the picker's page: a poll outside that page would
  // otherwise have no `currentVersion` to read an instrument from, and the
  // question selector would be empty for a reason nothing on the page explains.
  const poll = useLoad(() => (pollId ? getPoll(pollId) : Promise.resolve(null)), [pollId])
  const versionId = poll.data?.currentVersion?.id ?? null
  const instrument = useLoad(
    () => (versionId ? getInstrument(versionId) : Promise.resolve(null)), [versionId])

  const geo = useLoad(
    () => (pollId && level
      ? getResponseGeo(pollId, level, questionId || undefined)
      : Promise.resolve(null)),
    [pollId, level, questionId])

  const data = geo.data
  const cells = useMemo(
    () => [...(data?.cells ?? [])].sort((a, b) => b.interviews - a.interviews),
    [data],
  )
  const colours = useMemo(() => optionColours(cells), [cells])

  /**
   * Only questions that HAVE options.
   *
   * Derived from the instrument's own data, never from a list of type codes
   * written here — a code list that drifts from the server is the defect this
   * port exists to fix. A leading option is the most-chosen option of a choice
   * question; a NUMBER or OPEN_TEXT question has none, so asking for one returns
   * a map with no leader anywhere, which reads exactly like suppression. The
   * sentence under the control says so rather than leaving the omission silent.
   */
  const askable = useMemo(
    () => (instrument.data?.questions ?? [])
      .filter((q) => q.options.length > 0)
      .sort((a, b) => a.position - b.position),
    [instrument.data],
  )

  // A marker click scrolls its row into view. `block: 'nearest'` so a row already
  // on screen does not jump the page under the reader's cursor.
  useEffect(() => {
    if (!selected) return
    rows.current.get(selected)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [selected])

  if (!canRead) return <Forbidden what="the response map" />

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
    setSelected(null)
  }

  const levels = UNIT_TYPE_ORDER.filter((t) => t !== 'COUNTRY')
  const levelKnown = level === '' || (levels as string[]).includes(level)
  const questionKnown = questionId === '' || askable.some((q) => q.id === questionId)

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Response map"
        description="Where the fieldwork landed, aggregated to geography units."
        right={(
          <PollSelect
            polls={polls.data?.content ?? []}
            value={pollId}
            onChange={(id) => set('poll', id)}
          />
        )}
      />

      <ErrorStrip message={geo.error} />

      {!pollId ? (
        <Note tone="info">Pick a poll.</Note>
      ) : (
        <>
          <FilterBar>
            <label className="flex items-center gap-1.5 text-erp-xs">
              Level
              <select className="erp-input h-9" value={level}
                onChange={(e) => set('level', e.target.value)}>
                <option value="">Choose a level…</option>
                {levels.map((code) => (
                  <option key={code} value={code}>{UNIT_TYPE_LABEL[code] ?? code}</option>
                ))}
                {/* The URL is editable and shareable. A value the four do not
                    cover is kept in the control so the select does not silently
                    disagree with the address bar while the server explains. */}
                {!levelKnown && <option value={level}>{level} — not a level</option>}
              </select>
            </label>

            <label className="flex items-center gap-1.5 text-erp-xs">
              Question
              <select className="erp-input h-9 min-w-[240px]" value={questionId}
                onChange={(e) => set('question', e.target.value)}>
                <option value="">No question — counts only</option>
                {askable.map((q) => (
                  <option key={q.id} value={q.id}>{q.code} · {q.text}</option>
                ))}
                {!questionKnown && (
                  <option value={questionId}>Not on this instrument</option>
                )}
              </select>
            </label>
          </FilterBar>

          {/* Rule 3: the reason a control is limited goes in visible text beside
              it, because a `title` on a select nobody opens is unreachable. */}
          <p className="text-erp-xs text-muted-foreground">
            {versionId === null && !poll.loading
              ? 'This poll has no current version, so there is no instrument to pick a '
                + 'question from. The counts below do not need one.'
              : instrument.loading
                ? 'Reading the instrument…'
                : askable.length === 0
                  ? 'This instrument has no question with options, so there is none that '
                    + 'could lead a cell. A leading option is the most-chosen option of a '
                    + 'choice question.'
                  : 'Only questions with options are offered: a leading option is the '
                    + 'most-chosen option of a choice question, and a number or open-text '
                    + 'question has none.'}
          </p>

          {!level ? (
            <Note tone="info">
              Choose a level. It is not defaulted, here or on the server, because each of
              the four is a different aggregation of the same interviews and a different
              disclosure outcome — the same fieldwork withholds more cells at ward level
              than at county level, and picking one for you would pick that too.
            </Note>
          ) : geo.error ? (
            // Nothing below the strip. `useLoad` keeps the last good response
            // when the next one fails, so leaving the figures up would put the
            // previous level's cells under the refused level's name — the one
            // way this screen could state a location nobody sampled.
            null
          ) : geo.loading && !data ? (
            <LoadingBlock label="Loading the map…" />
          ) : data && (
            <>
              <KpiStrip>
                <MetricCard label="Interviews" value={count(data.totalInterviews)}
                  sub="submitted against this poll" />
                <MetricCard label="Mapped" value={count(data.mappedInterviews)}
                  sub="attributed to a unit, withheld cells included" />
                <MetricCard label="Unmapped" value={count(data.unmappedInterviews)}
                  sub="no fix, or a fix too imprecise to place"
                  accent={data.unmappedInterviews > 0} />
                <MetricCard label="Cells shown" value={count(data.cells.length)}
                  sub={cellsLabel(data.levelCode)} />
                <MetricCard label="Cells withheld" value={count(data.suppressedCells)}
                  sub={`below the ${data.minCellBase}-interview minimum`}
                  accent={data.suppressedCells > 0} />
              </KpiStrip>

              {/* THE DISCLOSURE. Once, in visible copy, above the map. */}
              <Note tone="info">
                Every point is the geography unit's own centroid — never a respondent's
                position, which is written once at capture and is read back by nothing.
                A unit holding fewer than {data.minCellBase} interviews is not returned at
                all: one interview in a ward, plotted with a leading candidate, is that
                respondent. The median accuracy in the table is how precise the
                interviewers&rsquo; own GPS fixes were, not how precisely the centroid is
                placed — a centroid is exact and corresponds to nothing on the ground.
                {' '}{data.suppressedCells > 0
                  ? `${count(data.suppressedCells)} ${data.suppressedCells === 1
                    ? 'unit was' : 'units were'} withheld on this view for that reason.`
                  : 'Nothing was withheld on this view.'}
              </Note>

              {!questionId && (
                <Note tone="info">
                  With no question chosen, no cell has a leading option and every circle is
                  drawn in ink. That is the correct answer rather than an empty legend — a
                  leader is a subgroup estimate, and there is no subgroup until a question
                  is picked.
                </Note>
              )}

              <Panel
                title="Map"
                description="Circle area is the interview count. Click a unit to find its row below."
              >
                <GeoCellMap
                  cells={cells}
                  colours={colours}
                  selectedUnitId={selected}
                  onSelect={setSelected}
                />
              </Panel>

              <Panel
                title="Cells"
                description="The same units the map draws, largest first. This table is the view of record."
              >
                <ErpTable head={(
                  <>
                    <Th>Unit</Th>
                    <Th className="text-right">Interviews</Th>
                    <Th className="text-right">Flagged</Th>
                    {/* Sub-labelled, not left as "Median accuracy": beside a
                        mapped point, "9.4 m" reads as the precision of the dot
                        — which would assert the dot IS a fix, the one
                        misreading the disclosure above exists to prevent. It is
                        the interviewers' GPS, not the centroid's. */}
                    <Th className="text-right">
                      <div>Median accuracy</div>
                      <div className="font-normal normal-case text-muted-foreground">
                        of the interviews&rsquo; GPS fixes
                      </div>
                    </Th>
                    <Th>Leading option</Th>
                    <Th className="text-right">Share</Th>
                  </>
                )}>
                  {cells.length === 0 ? (
                    <EmptyRow colSpan={6} message={
                      data.suppressedCells > 0
                        ? `No unit at this level reached ${data.minCellBase} interviews, so `
                          + 'every cell was withheld.'
                        : 'No interview on this poll has been attributed to a unit yet.'
                    } />
                  ) : cells.map((cell) => (
                    <CellRow
                      key={cell.unitId}
                      cell={cell}
                      colour={cell.leadingOptionId
                        ? colours.get(cell.leadingOptionId) ?? NO_LEADER_INK
                        : NO_LEADER_INK}
                      selected={selected === cell.unitId}
                      onSelect={() => setSelected(cell.unitId)}
                      register={(el) => rows.current.set(cell.unitId, el)}
                    />
                  ))}
                </ErpTable>
              </Panel>
            </>
          )}
        </>
      )}
    </div>
  )
}

/**
 * What the cells ARE at this level, taken from `levelCode` on the RESPONSE.
 *
 * Not from the `level` in the URL: those two are the same until the moment a
 * refetch is in flight, and labelling the figures on screen with the level being
 * loaded rather than the level they came from is how a county count comes to sit
 * under the word "wards".
 */
function cellsLabel(levelCode: string): string {
  const plural = UNIT_TYPE_PLURAL[levelCode]
  return plural ? `${plural.toLowerCase()} above the floor` : 'cells above the floor'
}

function CellRow({ cell, colour, selected, onSelect, register }: {
  cell: SurveyGeoCellDto
  colour: string
  selected: boolean
  onSelect: () => void
  register: (el: HTMLTableRowElement | null) => void
}) {
  const trail = cell.unitLabels.length > 0 ? cell.unitLabels.join(' › ') : null
  const accuracy = cell.medianAccuracyM === null ? '—' : `${num(cell.medianAccuracyM, 1)} m`

  return (
    <tr
      ref={register}
      onClick={onSelect}
      // The mirror of clicking a marker. Not a link and not a navigation — it
      // selects the same row the map highlights, so the two views cannot drift.
      // Focusable and Enter-activated, the same pattern the candidates table
      // uses: this table is the view of record and the map is the view that
      // needs a mouse, so a mouse-only row would leave keyboard and screen
      // reader users with no way to reach the selection at all. No
      // `aria-selected` — it is meaningless on a bare `<tr>` outside a grid,
      // and the selected row is already carried by its background and by the
      // map highlight it drives.
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onSelect() }}
      className={cn('cursor-pointer', selected
        ? 'bg-chocolate-soft/50'
        : 'hover:bg-muted/40')}
    >
      <Td>
        <div className="font-medium">{cell.unitName ?? 'Unnamed unit'}</div>
        {trail && <div className="text-erp-xs text-muted-foreground truncate">{trail}</div>}
      </Td>
      <Td className="text-right tabular-nums">{count(cell.interviews)}</Td>
      <Td className="text-right tabular-nums">{count(cell.flaggedInterviews)}</Td>
      <Td className="text-right tabular-nums">{accuracy}</Td>
      <Td>
        {cell.leadingOptionId ? (
          <span className="inline-flex items-center gap-1.5">
            <CellSwatch colour={colour} />
            {cell.leadingOptionLabel ?? cell.leadingOptionId}
          </span>
        ) : (
          // A dash, and the count in the row beside it stands. An absent leader
          // is "no estimate may be published here", not "nobody was interviewed".
          <span className="text-muted-foreground">—</span>
        )}
      </Td>
      <Td className="text-right tabular-nums">{pct(cell.leadingShare)}</Td>
    </tr>
  )
}
